import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import {
  createVoucher,
  deleteVoucher,
  getVouchers,
  getVouchersByEvent,
  updateVoucher,
} from "../../../../api/vouchers";
import {
  getTicketTypesByEvent
} from "../../../../api/ticketTypesApi";
import ToastContainer from "../../../../components/toastContainer";
import Toast from "../../../../components/toast";
import PageLoader from "../../../../components/pageLoader";
import VoucherFormModal from "./VoucherFormModal";
import DeleteConfirmationModal from "../../../../components/deleteModal";

const VouchersTab = ({ eventId, isViewOnly }) => { // ← CHANGE 1
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [vouchers, setVouchers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteVoucherId, setDeleteVoucherId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingVoucher, setEditingVoucher] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const [ticketTypes, setTicketTypes] = useState([]);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getVouchersByEvent(LIMIT, offset, eventId);
      setVouchers(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Failed to load vouchers.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getTicketTypesByEvent(LIMIT, offset, eventId);
      setTicketTypes(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      // setError("Failed to load ticket types.");
    } finally {
      setLoading(false);
    }
  };

  const handleVoucherSubmit = async (info) => {
    try {
      setSaving(true);

      const payload = {
        name: info.name,
        code: info.code,
        type: info.type,
        value: info.value,
        status: info.status,
        ticket_types: info.ticket_types,
        event: eventId,
      };

      if (editingVoucher) {
        await updateVoucher(editingVoucher.id, payload);
      } else {
        await createVoucher(payload);
      }

      setEditingVoucher(null);
      setShowAddModal(false);

      fetchVouchers();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVoucher = async () => {
    try {
      setDeleting(true);
      await deleteVoucher(deleteVoucherId);

      setDeleteVoucherId(null);

      fetchVouchers();
    } catch (err) {
      alert("Failed to delete the voucher");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchTicketTypes();
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">Manage vouchers</p>
        </div>

        {/* hide Create button for viewers */}
        {!isViewOnly && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <PlusIcon className="w-4 h-4" />
            Create voucher
          </button>
        )}
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Name</th>
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Value</th>
                <th className="px-6 py-4">Uses</th>
                <th className="px-6 py-4">Total Discounted</th>
                <th className="px-6 py-4">Generated by</th>
                <th className="px-6 py-4">Status</th>
                {/* hide Actions column for viewers */}
                {!isViewOnly && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v) => (
                <tr
                  key={v.id}
                  className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {v.name}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {v.code}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">{v.type}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{v.value}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{v.total_usage_count}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{v.total_usage_amount}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{v.created_by?.first_name} {v.created_by?.last_name}</td>
                  <td className="px-6 py-4 text-sm text-slate-300">{v.status}</td>

                  {/* hide edit/delete buttons for viewers */}
                  {!isViewOnly && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingVoucher(v)} className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition">
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteVoucherId(v.id)} className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition" title="Delete">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {vouchers.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No vouchers found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* modals only for non-viewers */}
      {!isViewOnly && (
        <>
          <VoucherFormModal
            isOpen={showAddModal || !!editingVoucher}
            onClose={() => { setShowAddModal(false); setEditingVoucher(null); }}
            onSubmit={handleVoucherSubmit}
            loading={saving}
            ticketTypes={ticketTypes}
            mode={editingVoucher ? "edit" : "create"}
            initialData={editingVoucher}
          />

          <DeleteConfirmationModal
            isOpen={!!deleteVoucherId}
            description="Are you sure you want to delete this voucher? This action cannot be undone."
            onClose={() => setDeleteVoucherId(null)}
            onConfirm={handleDeleteVoucher}
            loading={deleting}
          />
        </>
      )}

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default VouchersTab;
